import metadata from '../../../../metadata.json';

const HOT_WATER_PRODUCTION = metadata.hot_water_production;

const isPositiveNumber = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0;

/**
 * Effective hot-water production parameters for a building: the values entered on the
 * building form, otherwise metadata.json defaults (55 °C setpoint; efficiency per heating
 * type, COP for a heat pump). A missing or inconsistent value (setpoint not above the cold
 * water temperature, efficiency ≤ 0) falls back to the default.
 * Mirrors api/report_maker/report.py:hot_water_production_params.
 */
export const getHotWaterProductionParams = ({ waterHeatingType, hotWaterSetpoint, waterHeatingEfficiency }) => {
  const setpoint = isPositiveNumber(hotWaterSetpoint) && hotWaterSetpoint > HOT_WATER_PRODUCTION.cold_water_temperature
    ? hotWaterSetpoint
    : HOT_WATER_PRODUCTION.default_setpoint;
  const efficiency = isPositiveNumber(waterHeatingEfficiency)
    ? waterHeatingEfficiency
    : (HOT_WATER_PRODUCTION.default_efficiency[waterHeatingType] ?? 1);
  return { setpoint, efficiency };
};

/** kWh needed to heat 1 m³ from the cold water temperature to the given setpoint. */
export const kwhToHeatOneM3 = (setpoint) =>
  HOT_WATER_PRODUCTION.kwh_per_m3_per_degree * (setpoint - HOT_WATER_PRODUCTION.cold_water_temperature);

/**
 * Price of 1 m³ of hot water (water leaving the heater): water price + energy to heat it
 * from the cold water temperature to the setpoint, at the kWh price of the heating type
 * (fuel price for 'fuel', electricity otherwise — heat pump included), divided by the
 * efficiency. Mirrors api/report_maker/report.py:hot_water_price.
 * Returns null (never throws) when the required price inputs are missing/non-positive.
 */
export const computeHotWaterPrice = ({ waterPrice, electricityPrice, fuelPrice, waterHeatingType, hotWaterSetpoint, waterHeatingEfficiency }) => {
  if (waterPrice == null || waterPrice <= 0) return null;
  const energyPrice = waterHeatingType === 'fuel' ? fuelPrice : electricityPrice;
  if (energyPrice == null || energyPrice <= 0) return null;
  const { setpoint, efficiency } = getHotWaterProductionParams({ waterHeatingType, hotWaterSetpoint, waterHeatingEfficiency });
  return waterPrice + energyPrice * kwhToHeatOneM3(setpoint) / efficiency;
};

/**
 * technical_data.hot_water: faucet/shower connected to hot water, machine whose water is
 * heated (by the building's heater or by the machine itself). Missing (old equipment):
 * per-type default from metadata.json — connected for faucet/shower, not heated for a
 * machine; connected for a type without the toggle (WC, urinal), whose default share is 0.
 */
export const isHotWaterConnected = ({ equipmentType, technicalData }) => {
  const connected = technicalData?.hot_water;
  if (connected == null) return metadata.default_hot_water_connected?.[equipmentType] ?? true;
  return !!connected;
};

/**
 * Resolves the hot-water share (0-100 %) of a usage and where it comes from. Hot water is
 * the water leaving the heater. Order: equipment not connected → 0 ('not_connected');
 * otherwise the usage's own share ('usage', even 0); otherwise the equipment's typical
 * share ('equipment'); otherwise the per-type default of metadata.json ('default').
 * The legacy usage.hot_water boolean is never consulted.
 * Mirrors api/water_volume_computation.py:get_hot_water_ratio_with_source.
 */
export const getHotWaterRatioWithSource = ({ usage, equipmentType, technicalData }) => {
  if (!isHotWaterConnected({ equipmentType, technicalData })) return { value: 0, source: 'not_connected' };
  if (usage?.hot_water_ratio != null) return { value: usage.hot_water_ratio, source: 'usage' };
  if (technicalData?.hot_water_ratio != null) return { value: technicalData.hot_water_ratio, source: 'equipment' };
  return { value: metadata.default_hot_water_ratio?.[equipmentType] ?? 0, source: 'default' };
};

/**
 * Effective hot-water ratio (0-1 fraction) of a usage — see getHotWaterRatioWithSource.
 * `equipment` (with its technical_data) is needed for the connection toggle and the
 * equipment's typical share; `equipmentType` alone is accepted when only the type is known.
 */
export const getEffectiveHotWaterRatio = ({ usage, equipment, equipmentType }) =>
  getHotWaterRatioWithSource({
    usage,
    equipmentType: equipment?.type_equipment ?? equipmentType,
    technicalData: equipment?.technical_data,
  }).value / 100;

/**
 * Blends the cold and hot unit prices according to a hot-water ratio (0-1 fraction).
 * Returns null if the cold price is unavailable, or if any hot-water share is present
 * but the hot price can't be computed (propagates unavailability rather than
 * silently falling back to the cold price).
 */
export const getEffectiveUnitPrice = ({ ratio, coldWaterPrice, hotWaterPrice }) => {
  if (coldWaterPrice == null) return null;
  if (ratio > 0 && hotWaterPrice == null) return null;
  return ratio * (hotWaterPrice ?? 0) + (1 - ratio) * coldWaterPrice;
};
